import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { stripRollFromName } from '../utils/mcqMatch';

// Identity check, laid out like the Sort tab: imported students on the left
// (name mismatch in yellow, duplicate roll in red, missing sheet in gray),
// sheets whose roll matches nobody on the right. Fixing those is what opens
// the class table.

const cell = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  borderTop: '1px solid var(--border-color, #e5e7eb)',
  fontSize: '0.86rem'
};
const head = {
  padding: '10px 12px',
  textAlign: 'left',
  background: 'var(--surface, #F3F4F6)',
  fontWeight: 700,
  fontSize: '0.72rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#475569',
  borderBottom: '2px solid #d1d5db',
  position: 'sticky',
  top: 34,
  zIndex: 1
};

function imageLabel(row) {
  return row.omr_file_name || row.text_file_name || '';
}

function RollInput({ row, onUpdateField }) {
  return (
    <input
      key={`${row.id}-${row.roll_number || ''}`}
      defaultValue={row.roll_number || row.handwritten_roll_number || ''}
      onFocus={(e) => e.target.select()}
      onBlur={(e) => {
        const v = e.target.value.trim().toUpperCase();
        if (v !== (row.roll_number || '')) onUpdateField(row.id, 'roll_number', v);
      }}
      title="Correct the roll number so it matches a student"
      style={{ width: 72, padding: '3px 6px', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, fontWeight: 700 }}
    />
  );
}

function Actions({ row, openPreview, onDelete }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
      {(row.omr_scan_id || row.text_scan_id) && (
        <button
          onClick={() => openPreview(row, 'omr')}
          title="View the scanned sheet"
          style={iconBtn('#7C3AED')}
        >
          <FontAwesomeIcon icon={faEye} />
        </button>
      )}
      <button onClick={() => onDelete(row.id)} title="Delete this row" style={iconBtn('#EF4444')}>
        <FontAwesomeIcon icon={faTrash} />
      </button>
    </div>
  );
}

export default function McqReconciliation({ fillHeight = false, students, recon, loading, onUpdateField, onDelete, openPreview }) {
  if (loading) {
    return <div style={{ padding: 16, color: '#6b7280' }}>Loading student list…</div>;
  }
  if (!students || students.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        No imported student list for this folder's class and subject. Import one in the Students tab, then the name and roll check can run.
      </div>
    );
  }

  const { matched, unmatchedResults } = recon;
  const showUnmatched = unmatchedResults.length > 0;

  return (
    <div style={{
      display: 'flex',
      gap: 12,
      alignItems: fillHeight ? 'stretch' : 'flex-start',
      ...(fillHeight ? { flex: 1, minHeight: 0, overflow: 'hidden' } : null)
    }}>
      <style>{`
        .mcq-recon-table tbody tr:hover > td {
          background-image: linear-gradient(rgba(59,130,246,0.14), rgba(59,130,246,0.14));
        }
      `}</style>
      <div style={{ ...pane, flex: showUnmatched ? '1 1 58%' : '1 1 100%', ...(fillHeight ? { maxHeight: 'none', minHeight: 0 } : null) }}>
        <div style={banner('#7c3aed')}>Students from Imported List ({students.length})</div>
        <table className="mcq-recon-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={head}>Expected</th>
              <th style={head}>AI Read</th>
              <th style={head}>Image</th>
              <th style={head}>Roll</th>
              <th style={head}>Status</th>
              <th style={{ ...head, textAlign: 'center' }}>Acts</th>
            </tr>
          </thead>
          <tbody>
            {students.map((stu) => {
              const bucket = matched.find(m => m.expected === stu);
              const aiRows = bucket ? bucket.aiRows : [];
              if (aiRows.length === 0) {
                return (
                  <tr key={`miss-${stu.id}`} style={{ background: '#F9FAFB' }}>
                    <td style={cell}><strong>{stu.student_name}</strong></td>
                    <td style={cell} colSpan={3}><em style={{ color: '#6b7280' }}>— no AI match —</em></td>
                    <td style={cell}><span style={{ color: '#6b7280', fontWeight: 700 }}>✗ Missing</span></td>
                    <td style={cell} />
                  </tr>
                );
              }
              return aiRows.map((r, idx) => {
                const bg = r._isDuplicateRoll ? '#FEE2E2' : r._nameMismatch ? '#FEF9C3' : 'transparent';
                const expectedName = stripRollFromName(stu.student_name);
                return (
                  <tr key={`${stu.id}-${r.id}`} style={{ background: bg }}>
                    <td style={cell}>{idx === 0 ? <strong>{stu.student_name}</strong> : <span style={{ color: '#9ca3af' }}>↳</span>}</td>
                    <td style={cell}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span>{r.student_name || '—'}</span>
                        {r._nameMismatch && !r._isDuplicateRoll && (
                          <button
                            onClick={() => onUpdateField(r.id, 'student_name', expectedName)}
                            title={`Use the list name "${expectedName}"`}
                            style={tickBtn}
                          >
                            ✓
                          </button>
                        )}
                      </span>
                    </td>
                    <td style={{ ...cell, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={imageLabel(r)}>
                      {imageLabel(r) || '—'}
                    </td>
                    <td style={cell}>
                      {r._isDuplicateRoll
                        ? <RollInput row={r} onUpdateField={onUpdateField} />
                        : (r.roll_number || '—')}
                    </td>
                    <td style={cell}>
                      {r._isDuplicateRoll ? (
                        <span style={{ color: '#b91c1c', fontWeight: 700 }}>⚠ Duplicate roll{r._nameMismatch ? ' + name' : ''}</span>
                      ) : r._nameMismatch ? (
                        <span style={{ color: '#a16207', fontWeight: 700 }}>⚠ Name mismatch</span>
                      ) : (
                        <span style={{ color: '#059669', fontWeight: 700 }}>✓ OK</span>
                      )}
                    </td>
                    <td style={cell}><Actions row={r} openPreview={openPreview} onDelete={onDelete} /></td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      {showUnmatched && (
        <div style={{ ...pane, flex: '1 1 42%', borderColor: '#fecaca', ...(fillHeight ? { maxHeight: 'none', minHeight: 0 } : null) }}>
          <div style={banner('#dc2626')}>⚠ Unmatched AI Rows ({unmatchedResults.length})</div>
          <table className="mcq-recon-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={head}>AI Read</th>
                <th style={head}>Image</th>
                <th style={head}>Roll</th>
                <th style={{ ...head, textAlign: 'center' }}>Acts</th>
              </tr>
            </thead>
            <tbody>
              {unmatchedResults.map((r) => (
                <tr key={`un-${r.id}`} style={{ background: r._isDuplicateRoll ? '#FEE2E2' : '#FEF2F2' }}>
                  <td style={cell}>
                    {r._isDuplicateRoll && (
                      <span style={dupChip}>⚠ DUP</span>
                    )}{' '}
                    {r.student_name || '—'}
                  </td>
                  <td style={{ ...cell, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={imageLabel(r)}>
                    {imageLabel(r) || '—'}
                  </td>
                  <td style={cell}><RollInput row={r} onUpdateField={onUpdateField} /></td>
                  <td style={cell}><Actions row={r} openPreview={openPreview} onDelete={onDelete} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const pane = {
  minWidth: 0,
  maxHeight: '70vh',
  overflow: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: 'white'
};

function banner(color) {
  return {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: color,
    color: 'white',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: '0.85rem'
  };
}

const tickBtn = {
  background: '#10b981',
  color: 'white',
  border: 'none',
  padding: '2px 7px',
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 700,
  flexShrink: 0
};

const dupChip = {
  background: '#b91c1c',
  color: 'white',
  fontSize: '0.6rem',
  fontWeight: 700,
  padding: '1px 5px',
  borderRadius: 4
};

function iconBtn(color) {
  return {
    background: color,
    color: 'white',
    border: 'none',
    padding: '5px 8px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12
  };
}
